import { Link, useNavigate } from 'react-router-dom';
import { Menu, Users, Eye, ShieldCheck, Grape, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { isV3 } from '../../hooks/useDesignVersion';
import { NAV_ITEMS, NAV_GROUPS } from '../../constants/navigation';
import { NavItem } from './NavItem';
import { NavGroup } from './NavGroup';
import type { UserRole } from '../../types/api';

const ROLE_CONFIG: Record<string, { icon: typeof Users; label: string }> = {
  broker: { icon: Users, label: 'Broker' },
  underwriter: { icon: Eye, label: 'Underwriter' },
  admin: { icon: ShieldCheck, label: 'Admin' },
  winery_manager: { icon: Grape, label: 'Winery Manager' },
};

export function GlobalNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useTheme();

  const userRole = (user?.role || 'underwriter') as UserRole;

  const varItem = NAV_ITEMS.find(i => i.key === 'var')!;

  return (
    <>
      <nav className={`bg-white shadow-sm border-b border-gray-200 dark:bg-slate-900 dark:border-slate-800 ${isV3() ? 'md:hidden' : 'lg:hidden'}`} data-testid="global-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              {/* Logo */}
              <Link to="/dashboard" className="flex items-center" data-testid="nav-logo">
                <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="nav-flame" x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#f97316"/>
                      <stop offset="1" stopColor="#f59e0b"/>
                    </linearGradient>
                  </defs>
                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" fill="url(#nav-flame)" stroke="url(#nav-flame)" strokeWidth="0.5"/>
                </svg>
                <span data-testid="nav-logo-text" className="ml-2 text-xl font-bold text-gray-900 dark:text-slate-100 tracking-tight whitespace-nowrap">
                  FIREBREAK <span className="text-gray-500 dark:text-slate-400 font-light">BACCHUS</span>
                </span>
              </Link>

              {/* Desktop Navigation - Rule of Threes */}
              <div className="hidden md:ml-8 md:flex md:items-center md:gap-1">
                <NavItem item={varItem} />
                <span className="text-gray-300 dark:text-slate-600 px-2">|</span>
                {NAV_GROUPS.map((group, idx) => (
                  <div key={group.key} className="flex items-center gap-1">
                    <NavGroup group={group} userRole={userRole} />
                    {idx < NAV_GROUPS.length - 1 && (
                      <span className="text-gray-300 dark:text-slate-600 px-2">|</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center gap-3">
              {(() => {
                const cfg = ROLE_CONFIG[userRole] || ROLE_CONFIG.underwriter;
                const Icon = cfg.icon;
                return (
                  <span
                    data-testid="nav-role-badge"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-700 text-white dark:bg-slate-700 shadow-sm"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {cfg.label}
                  </span>
                );
              })()}
              <button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button
                data-testid="logout-button"
                onClick={() => logout()}
                className="text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>

            {/* Mobile: theme toggle + hamburger */}
            <div className="flex md:hidden items-center gap-1">
              <button
                data-testid="theme-toggle-mobile"
                onClick={toggleTheme}
                className="p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800 min-h-[44px] min-w-[44px] flex items-center justify-center"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <button
                data-testid="nav-hamburger"
                type="button"
                onClick={() => navigate('/more')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Open navigation menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

    </>
  );
}
